import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Monitor, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { PageHeader, Empty } from "@/components/kit";

const ChangePassword = () => {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error("As senhas não coincidem");
    try { await api.post("/auth/change-password", f); toast.success("Senha alterada. Outras sessões foram encerradas."); setF({ current_password: "", new_password: "", confirm: "" }); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="surface p-5 md:p-7 space-y-4" data-testid="change-password-form">
      <div className="eyebrow">Alterar senha</div>
      {[["current_password", "Senha atual"], ["new_password", "Nova senha (mín. 8, letras e números)"], ["confirm", "Confirmar nova senha"]].map(([k, l]) => (
        <div key={k} className="space-y-2"><Label>{l}</Label><Input type="password" required className="h-11" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`pw-${k}-input`} /></div>))}
      <Button className="h-11" data-testid="change-password-submit">Alterar senha</Button>
    </form>
  );
};

const TwoFactor = () => {
  const { user, refreshMe } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const start = async () => { try { const { data } = await api.post("/auth/2fa/setup"); setSetup(data); } catch (e) { toast.error(errMsg(e)); } };
  const enable = async () => { try { await api.post("/auth/2fa/enable", { code }); toast.success("2FA ativado"); setSetup(null); setCode(""); refreshMe(); } catch (e) { toast.error(errMsg(e)); } };
  const disable = async () => { try { await api.post("/auth/2fa/disable", { password: pw, code }); toast.success("2FA desativado"); setCode(""); setPw(""); refreshMe(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="surface p-5 md:p-7 space-y-4" data-testid="twofa-card">
      <div className="flex items-center justify-between"><div className="eyebrow">Autenticação em dois fatores</div>
        <span className={`text-xs font-bold ${user.totp_enabled ? "up" : "text-amber-500"}`} data-testid="twofa-status">{user.totp_enabled ? "ATIVADA" : "DESATIVADA"}</span></div>
      <p className="text-sm text-muted-foreground">Use um aplicativo autenticador (Google Authenticator, Authy, Microsoft Authenticator) para gerar códigos de 6 dígitos no login e em saques.</p>
      {!user.totp_enabled && !setup && <Button onClick={start} className="h-11" data-testid="twofa-setup-btn">Configurar 2FA</Button>}
      {setup && (
        <div className="space-y-3">
          <ol className="text-sm list-decimal pl-5 space-y-1"><li>Abra o aplicativo autenticador.</li><li>Escaneie o QR Code ou digite a chave.</li><li>Informe o código gerado.</li></ol>
          <img src={setup.qr} alt="QR Code 2FA" className="h-44 w-44 rounded-xl bg-white p-2" data-testid="twofa-qr" />
          <div className="num text-xs break-all rounded-lg bg-muted p-2" data-testid="twofa-secret">{setup.secret}</div>
          <div className="flex gap-2"><Input inputMode="numeric" maxLength={6} placeholder="000000" className="h-11 num" value={code} onChange={(e) => setCode(e.target.value)} data-testid="twofa-code-input" />
            <Button onClick={enable} className="h-11" data-testid="twofa-enable-btn">Ativar</Button></div>
        </div>
      )}
      {user.totp_enabled && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input type="password" placeholder="Senha" className="h-11" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="twofa-disable-password" />
          <Input inputMode="numeric" maxLength={6} placeholder="Código" className="h-11 num" value={code} onChange={(e) => setCode(e.target.value)} data-testid="twofa-disable-code" />
          <Button variant="outline" className="h-11" onClick={disable} data-testid="twofa-disable-btn">Desativar 2FA</Button>
        </div>
      )}
    </div>
  );
};

const Sessions = () => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/auth/sessions").then((r) => setRows(r.data));
  usePoll(() => load().catch(() => {}), 30000);
  const revoke = async (id) => { await api.delete(`/auth/sessions/${id}`); toast.success("Sessão encerrada"); load(); };
  const others = async () => { await api.post("/auth/sessions/revoke-others"); toast.success("Outras sessões encerradas"); load(); };
  return (
    <div className="surface p-5 md:p-7" data-testid="sessions-card">
      <div className="flex items-center justify-between mb-3"><div className="eyebrow">Dispositivos e sessões ativas</div>
        <Button size="sm" variant="outline" onClick={others} data-testid="revoke-other-sessions-btn">Encerrar outras sessões</Button></div>
      <div className="divide-y divide-border">
        {rows.map((s) => (
          <div key={s.id} className="flex items-center gap-3 py-3 text-sm" data-testid={`session-row-${s.id}`}>
            <Monitor className="h-5 w-5 text-muted-foreground" />
            <div className="flex-1"><div className="font-medium">{s.device} {s.current && <span className="ml-1 text-xs up">(este dispositivo)</span>}</div>
              <div className="text-xs text-muted-foreground">IP {s.ip} · iniciada {fmtDateTime(s.created_at)} · {s.remember ? "permanecer conectado" : "sessão do navegador"}</div></div>
            {!s.current && <Button size="sm" variant="ghost" onClick={() => revoke(s.id)} data-testid={`revoke-session-${s.id}`}>Encerrar</Button>}
          </div>
        ))}
      </div>
    </div>
  );
};

const History = () => {
  const [rows, setRows] = useState([]);
  usePoll(() => api.get("/auth/login-history").then((r) => setRows(r.data)).catch(() => {}), 60000);
  return (
    <div className="surface p-5 md:p-7" data-testid="login-history-card">
      <div className="eyebrow mb-3">Histórico de acessos</div>
      {rows.length === 0 ? <Empty text="Sem registros." /> : (
        <div className="divide-y divide-border max-h-96 overflow-y-auto">
          {rows.map((h) => (
            <div key={h.id} className="flex items-center gap-3 py-2.5 text-sm">
              {h.success ? <CheckCircle2 className="h-4 w-4 up" /> : <XCircle className="h-4 w-4 down" />}
              <div className="flex-1">{h.device} · <span className="num text-xs">{h.ip}</span>{h.reason && <span className="text-xs down"> · {h.reason}</span>}</div>
              <span className="text-xs text-muted-foreground">{fmtDateTime(h.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Consents = () => {
  const { user, setUser } = useAuth();
  const c = user.consents || {};
  const set = async (k, v) => { try { const { data } = await api.put("/auth/consents", { [k]: v }); setUser({ ...user, ...data }); toast.success("Consentimento atualizado"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="surface p-5 md:p-7 space-y-4 text-sm" data-testid="consents-card">
      <div className="eyebrow">Privacidade e consentimentos (LGPD)</div>
      {[["terms", "Aceito os Termos de uso", "/legal/termos"], ["privacy", "Aceito a Política de privacidade", "/legal/privacidade"], ["marketing", "Receber comunicações de marketing", null],
        ["cookies_analytics", "Cookies de análise", "/legal/cookies"]].map(([k, l, href]) => (
        <div key={k} className="flex items-center justify-between gap-4">
          <span>{l} {href && <Link to={href} className="text-primary text-xs underline">ler</Link>}
            {c[k]?.at && <span className="block text-xs text-muted-foreground">Atualizado em {fmtDateTime(c[k].at)}</span>}</span>
          <Switch checked={!!c[k]?.value} onCheckedChange={(v) => set(k, v)} data-testid={`consent-${k}-switch`} />
        </div>
      ))}
    </div>
  );
};

export default function Security() {
  const { user } = useAuth();
  return (
    <div data-testid="security-page">
      <PageHeader eyebrow="Proteção da conta" title="Segurança" desc="Senhas nunca são armazenadas no navegador. Sua sessão usa cookies seguros (httpOnly) que você pode encerrar a qualquer momento." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <TwoFactor />
        <ChangePassword />
        <Sessions />
        <History />
        {user.role === "client" && <Consents />}
      </div>
    </div>
  );
}
